'use client';

import { useMemo } from 'react';
import { Button, TextButton } from '@galapagos/design-system/web';
import { typicalWeekConfirmedAnswers } from '@galapagos/shared';
import { useTranslation } from 'react-i18next';
import { countsByWeekday, weekSummary } from '../../baseline/baseline-week';
import {
  baselineExitStep,
  baselinePassThroughStep,
  useBaselineReads,
  useBaselineSave,
  useReturnWhenWeekMissing,
} from '../../baseline/useBaselineStep';
import { FormActions } from '../../components/FormActions';
import { PrivateFields } from '../../components/PrivateFields';
import { UnitGuideCallout } from '../../components/UnitGuide';
import { WeekSummary } from '../../components/WeekSummary';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import { useStepHeading } from '../../hooks/useStepHeading';
import { memberTimeZone } from '../../wrap-up/booking-slots';
import { gated } from '../wrap-up/gated';
import { BaselineFallbackActions, BaselineFrame, useBaselineStatus } from './BaselineFrame';

/**
 * `/baseline-typical/`: the saved week summed up, and whether it was
 * typical. "Yes" saves that and leaves the segment; "No" asks for a usual
 * week and saves nothing yet.
 */
function BaselineTypicalStep() {
  const { t } = useTranslation('signup');
  const title = t('baseline.typical.title');
  const headingRef = useStepHeading(title);
  const { goToStep } = useSignupNavigation();
  const reads = useBaselineReads();
  useReturnWhenWeekMissing(reads);
  const zone = useMemo(() => memberTimeZone(), []);
  const save = useBaselineSave();
  const { status, statusTone } = useBaselineStatus(reads, save);
  const description = t('baseline.typical.description');

  // With no complete week saved the step is on its way back to the week
  // step, so it shows only its loading status.
  if (reads.status !== 'ready' || reads.data.week === null) {
    return (
      <BaselineFrame
        title={title}
        description={description}
        headingRef={headingRef}
        status={reads.status === 'ready' ? t('baseline.loading') : status}
        statusTone={statusTone}
      >
        {reads.status === 'ready' ? null : (
          <BaselineFallbackActions
            read={reads}
            passThroughTo={baselinePassThroughStep('baseline-typical')}
          />
        )}
      </BaselineFrame>
    );
  }

  const { wording, week: saved, capture } = reads.data;
  const summary = weekSummary(countsByWeekday(saved.anchorDate, zone, saved.counts));
  const yesNo = capture.mode === 'yes-no';
  return (
    <BaselineFrame
      title={title}
      description={description}
      headingRef={headingRef}
      status={status}
      statusTone={statusTone}
      body={
        <>
          {wording.unitGuide && !yesNo ? (
            <UnitGuideCallout
              guide={wording.unitGuide}
              data-testid="signup-baseline-typical-guide-callout"
            />
          ) : null}
          {/* The week's counts are health information: no autocapture or RUM click here. */}
          <PrivateFields>
            <WeekSummary summary={summary} wording={wording} yesNo={yesNo} />
          </PrivateFields>
        </>
      }
    >
      <FormActions>
        <Button
          size="L"
          type="Primary"
          label={t('baseline.typical.yes')}
          state={save.pending ? 'Loading' : 'Default'}
          actionId="signup.baseline-typical-yes.activate"
          data-testid="signup-baseline-typical-yes-button"
          onPress={() => save.save(typicalWeekConfirmedAnswers(), baselineExitStep())}
        />
        <TextButton
          label={t('baseline.typical.no')}
          actionId="signup.baseline-typical-no.activate"
          data-testid="signup-baseline-typical-no-button"
          onPress={() => {
            if (!save.pending) goToStep('baseline-adjust');
          }}
        />
      </FormActions>
    </BaselineFrame>
  );
}

export const BaselineTypicalScreen = gated(BaselineTypicalStep, 'baseline-typical');
